import NurturingHistory, { CreateShortUrlRequest } from 'app/apiService/nurturingHistory/nurturingHistoryApiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { metricTypeEnum } from 'app/pages/OpportunitiesPage/data/type';

type NurturingHistoryRow = Record<string, unknown> | undefined | null;

/** Prefer CRM email fields; ignore ID-shaped values mislabeled as email on some endpoints. */
export function getNurturingHistoryEmailFromRow(row: NurturingHistoryRow): string {
  if (!row) return '';

  const candidates = [
    row.Email_mailshake,
    row.email_address,
    row.email,
    row.Email,
    row.emailAddress,
  ];

  for (const raw of candidates) {
    if (raw == null) continue;
    const value = String(raw).trim();
    if (!value) continue;
    if (value.includes('@')) return value;
    if (/^[a-f0-9]{24}$/i.test(value)) continue;
  }

  return '';
}

export function getNurturingHistoryOpportunityIdFromRow(
  row: NurturingHistoryRow,
): string {
  if (!row) return '';
  const id = row.opportunity_id ?? row.opportunityId ?? row._id ?? row.id;
  return id != null ? String(id).trim() : '';
}

export function buildNurturingHistoryShortUrlPayload(params: {
  rowDetails?: NurturingHistoryRow;
  opportunityId: string;
  dealerId: string;
  analytics?: boolean;
  emailAddress?: string;
}): CreateShortUrlRequest {
  const explicitEmail = params.emailAddress?.trim() ?? '';
  const emailAddress =
    (explicitEmail.includes('@') ? explicitEmail : '') ||
    getNurturingHistoryEmailFromRow(params.rowDetails);

  if (!emailAddress) {
    throw new Error('Email address is required for nurturing history');
  }

  if (!params.opportunityId) {
    throw new Error('Opportunity ID is required for nurturing history');
  }

  if (!params.dealerId) {
    throw new Error('Dealer ID is required for nurturing history');
  }

  const row = params.rowDetails;
  const originatingDealerId =
    (row?.originating_dealer_id != null
      ? String(row.originating_dealer_id).trim()
      : '') ||
    (row?.dealer_id != null ? String(row.dealer_id).trim() : '') ||
    params.dealerId;

  return {
    emailAddress,
    opportunityId: params.opportunityId,
    dealerId: params.dealerId,
    analytics: params.analytics ?? true,
    originating_dealer_id: originatingDealerId,
  };
}

export interface NurturingHistoryParams {
  emailAddress?: string;
  opportunityId: string;
  dealerId: string;
  rowDetails?: any;
  analytics?: boolean;
  /** Segment cohort id when opening history from segment Lead List (breadcrumb + return navigation). */
  segmentGroupId?: string;
  segmentDisplayName?: string;
}

/** Ensures Lead List crumb has segment label, deep link, and filter.groupId for return navigation. */
function enrichBreadcrumbsWithLeadListSegment(
  crumbs: any[],
  segmentGroupId?: string,
  segmentDisplayName?: string,
): any[] {
  if (!crumbs?.length || !segmentGroupId) {
    return crumbs || [];
  }
  const name = (segmentDisplayName || '').trim();
  const leadLink = `/opportunities?view=leads&segmentId=${encodeURIComponent(segmentGroupId)}`;

  return crumbs.map(c => {
    const labelStr = typeof c.label === 'string' ? c.label : '';
    const linkStr = c.link ? String(c.link) : '';
    const looksLikeLeadList =
      labelStr === 'Lead List' ||
      labelStr.startsWith('Lead List (') ||
      labelStr.startsWith('Lead List(') ||
      linkStr.includes('view=leads');

    if (!looksLikeLeadList) {
      return { ...c };
    }

    return {
      ...c,
      label: name
        ? `Lead List (${name})`
        : labelStr.includes('(')
          ? labelStr
          : 'Lead List',
      link: leadLink,
      filter: {
        ...(typeof c.filter === 'object' && c.filter ? c.filter : {}),
        metricType: metricTypeEnum.segment,
        groupId: segmentGroupId,
      },
    };
  });
}

export const handleNurturingHistoryRedirect = async (
  params: NurturingHistoryParams,
  dispatch: any,
  history: any,
  currentBreadcrumbs?: any[],
): Promise<void> => {
  try {
    const payload = buildNurturingHistoryShortUrlPayload({
      rowDetails: params.rowDetails,
      opportunityId: params.opportunityId,
      dealerId: params.dealerId,
      analytics: params.analytics,
      emailAddress: params.emailAddress,
    });
    const { emailAddress } = payload;

    console.log('Creating short URL with payload:', payload);

    // Call API to create short URL
    const response = await NurturingHistory.createShortUrl(payload);

    if (response.statusCode !== 201 && response.statusCode !== 200) {
      throw new Error(response.message || 'Failed to create short URL');
    }

    const shortUrl = response.body?.shortUrl;

    if (!shortUrl) {
      throw new Error('Short URL not received from API response');
    }

    console.log('Short URL created:', shortUrl);

    // Extract URL ID from the short URL
    const urlId = NurturingHistory.extractUrlId(shortUrl);

    if (!urlId) {
      throw new Error('Could not extract URL ID from short URL');
    }

    console.log('Extracted URL ID:', urlId);

    const baseCrumbs = (currentBreadcrumbs || []).map(breadcrumb => ({
      ...breadcrumb,
      isActive: false,
    }));
    const crumbsWithSegment = enrichBreadcrumbsWithLeadListSegment(
      baseCrumbs,
      params.segmentGroupId,
      params.segmentDisplayName,
    );

    // Prepare breadcrumbs for the nurturing history page
    const nurturingHistoryBreadcrumbs = [
      ...crumbsWithSegment,
      {
        label: `Nurturing History (${emailAddress})`,
        link: `/url/${urlId}`,
        isActive: true,
      },
    ];

    // Navigate to the URL with proper breadcrumbs
    // Use window.location for external navigation from AI Avatar Layout
    setTimeout(() => {
      const targetUrl = `/url/${urlId}`;
      const currentPath = window.location.pathname;
      
      // Check if we're in AI Avatar Layout
      const isInAIAvatarLayout = currentPath.includes('/ai-avatar-layout');
      
      if (isInAIAvatarLayout) {
        // Use window.location to break out of nested routing
        window.location.href = targetUrl;
      } else {
        // Use history.push for normal navigation
        history.push(targetUrl, {
          breadcrumbsList: nurturingHistoryBreadcrumbs,
          fromPage: 'Opportunities',
          emailAddress,
          opportunityId: params.opportunityId,
          dealerId: params.dealerId
        });
      }
    }, 1000);

  } catch (error) {
    console.error('Error creating nurturing history link:', error);

    // Show error notification
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: error instanceof Error ? error.message : 'Failed to create nurturing history link',
      }),
    );
  }
};

export default {
  handleNurturingHistoryRedirect,
}; 